import React from 'react';
import { CalendarClock } from 'lucide-react';
import { formatDateOnlyValue } from '../../utils/localDateTime';
import { EXPIRY_STATUS_META, daysUntil, expiryStatus, sortLotesFefo } from '../../utils/stockLotes';

// Lotes en el ajuste manual de stock (Vencimientos y lotes, fase 1).
//   Ingreso: pide vencimiento (obligatorio) y lote (opcional).
//   Egreso:  muestra los lotes con su semáforo y la cantidad que sale de cada uno.
//            Viene preseleccionado (FEFO, o solo vencidos si es baja por
//            vencimiento): si el usuario no toca nada, el ajuste sigue siendo de un paso.
// Componente controlado: value = { porVencimiento, entrada: {fecha_vencimiento, lote_codigo},
//                                  salidas: {[lote_id]: cantidad}, manual }

const fmt = (n) => Number(n || 0).toLocaleString('es-AR', { maximumFractionDigits: 3 });

const LotesAjustePanel = ({ tipo, cantidad, lotes = [], loading = false, value, onChange, alertDays = 30 }) => {
  const ordenados = sortLotesFefo(lotes);
  const set = (patch) => onChange({ ...value, ...patch });
  const totalSalidas = Object.values(value?.salidas || {}).reduce((a, c) => a + (Number(c) || 0), 0);
  const visibles = value?.porVencimiento
    ? ordenados.filter((l) => l.fecha_vencimiento && daysUntil(l.fecha_vencimiento) <= 0)
    : ordenados;

  if (tipo === 'entrada') {
    return (
      <div className="mt-3 rounded-xl border border-violet-200 bg-violet-50/60 p-3">
        <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-violet-700 mb-2">
          <CalendarClock size={12} /> Lote que entra
        </div>
        <div className="flex flex-wrap gap-2">
          <label className="flex flex-col gap-0.5">
            <span className="text-[8px] font-black text-slate-400 uppercase">Vencimiento *</span>
            <input
              type="date"
              value={value?.entrada?.fecha_vencimiento || ''}
              onChange={(e) => set({ entrada: { ...(value?.entrada || {}), fecha_vencimiento: e.target.value } })}
              className={`h-9 px-2 rounded-lg border bg-white text-[11px] font-bold ${value?.entrada?.fecha_vencimiento ? 'border-slate-200' : 'border-amber-300'}`}
            />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[8px] font-black text-slate-400 uppercase">Lote (opcional)</span>
            <input
              type="text"
              value={value?.entrada?.lote_codigo || ''}
              onChange={(e) => set({ entrada: { ...(value?.entrada || {}), lote_codigo: e.target.value.toUpperCase() } })}
              className="h-9 w-36 px-2 rounded-lg border border-slate-200 bg-white text-[11px] font-bold uppercase"
            />
          </label>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-violet-200 bg-violet-50/60 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-violet-700">
          <CalendarClock size={12} /> Lotes que salen
        </div>
        <label className="flex items-center gap-1.5 text-[9px] font-black uppercase text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(value?.porVencimiento)}
            onChange={(e) => set({ porVencimiento: e.target.checked, manual: false })}
          />
          Baja por vencimiento
        </label>
      </div>

      {loading ? (
        <p className="text-[10px] font-bold text-slate-400">Cargando lotes…</p>
      ) : visibles.length === 0 ? (
        <p className="text-[10px] font-bold text-slate-500">
          {value?.porVencimiento ? 'No hay lotes vencidos ni que venzan hoy en esta sucursal.' : 'No hay lotes con stock en esta sucursal.'}
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          {visibles.map((l) => {
            const estado = expiryStatus(l.fecha_vencimiento, alertDays);
            const meta = EXPIRY_STATUS_META[estado];
            return (
              <div key={l.id} className="flex items-center gap-2 bg-white rounded-lg border border-slate-100 px-2 py-1.5">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-black uppercase text-slate-700 truncate">
                    {l.lote_codigo || (l.fecha_vencimiento ? 'Sin código' : 'Sin lote')}
                    <span className="ml-1.5 text-slate-400 font-bold normal-case">
                      {l.fecha_vencimiento ? `vence ${formatDateOnlyValue(l.fecha_vencimiento)}` : 'sin fecha'} · {fmt(l.cantidad_actual)} u.
                    </span>
                  </div>
                </div>
                <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded border ${meta.className}`}>{meta.label}</span>
                <input
                  type="number"
                  min="0"
                  max={l.cantidad_actual}
                  step="any"
                  value={value?.salidas?.[l.id] ?? ''}
                  onChange={(e) => set({
                    manual: true,
                    salidas: { ...(value?.salidas || {}), [l.id]: e.target.value === '' ? '' : Math.min(Number(l.cantidad_actual), Math.max(0, Number(e.target.value))) },
                  })}
                  placeholder="0"
                  className="w-16 h-8 px-2 rounded-lg border border-slate-200 text-[11px] font-bold text-right"
                />
              </div>
            );
          })}
        </div>
      )}

      <div className={`mt-2 text-[9px] font-bold ${
        value?.porVencimiento && Math.abs(totalSalidas - Number(cantidad || 0)) > 1e-6 ? 'text-rose-600' : 'text-slate-500'
      }`}>
        Elegido: {fmt(totalSalidas)} de {fmt(cantidad)} u.
        {!value?.porVencimiento && totalSalidas < Number(cantidad || 0) ? ' — el resto sale por lo que vence antes' : ''}
      </div>
    </div>
  );
};

export default LotesAjustePanel;
